import React, { useState, useEffect } from 'react';
import { Card, Form, InputNumber, Button, message, Spin, Typography, Divider, Row, Col, Statistic, Descriptions } from 'antd';
import { SettingOutlined, ThunderboltOutlined, DollarOutlined } from '@ant-design/icons';
import { getQuotaDefaults, updateQuotaDefaults } from '../services/adminService';

const { Title, Paragraph } = Typography;

const MODEL_COSTS = {
  'claude-haiku-4-5':  { label: 'Claude Haiku 4.5',  multiplier: 1 },
  'claude-sonnet-4-6': { label: 'Claude Sonnet 4.6', multiplier: 3 },
  'claude-opus-4-6':   { label: 'Claude Opus 4.6',   multiplier: 5 },
};

function AISettings() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [form] = Form.useForm();

  const loadDefaults = async () => {
    setLoading(true);
    try {
      const d = await getQuotaDefaults();
      setDefaults(d);
      form.setFieldsValue({
        user_daily_quota:    d.User    ?? 1_000_000,
        creator_daily_quota: d.Creator ?? 2_000_000,
      });
    } catch (err) {
      console.error('Failed to load AI settings:', err);
      message.error('Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDefaults(); }, []); // eslint-disable-line

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        User:    values.user_daily_quota,
        Creator: values.creator_daily_quota,
        Admin:   -1,   // Admin is always unlimited
      };
      const updated = await updateQuotaDefaults(payload);
      setDefaults(updated);
      message.success('AI quota defaults updated');
    } catch (err) {
      message.error('Failed to update defaults');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 16 }}><SettingOutlined /> AI Settings</Title>

      <Spin spinning={loading}>
        <Row gutter={24}>
          <Col span={12}>
            <Card title={<><ThunderboltOutlined /> Token Quota Defaults</>}>
              <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                Set the default daily token quotas for new users. Existing users keep their current quotas unless individually updated.
              </Paragraph>

              <Form form={form} layout="vertical" onFinish={handleSave}>
                <Form.Item label="User Daily Quota" name="user_daily_quota"
                  rules={[{ required: true, message: 'Required' }]}
                  extra="Number of weighted tokens a User can consume per day.">
                  <InputNumber min={1000} max={100000000} step={100000} style={{ width: '100%' }}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={v => v.replace(/,/g, '')} />
                </Form.Item>

                <Form.Item label="Creator Daily Quota" name="creator_daily_quota"
                  rules={[{ required: true, message: 'Required' }]}
                  extra="Number of weighted tokens a Creator can consume per day.">
                  <InputNumber min={1000} max={100000000} step={100000} style={{ width: '100%' }}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={v => v.replace(/,/g, '')} />
                </Form.Item>

                <Form.Item>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Admin accounts always have unlimited token quota.
                  </Typography.Text>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button type="primary" htmlType="submit" loading={saving}>Save Defaults</Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          <Col span={12}>
            <Card title={<><DollarOutlined /> Model Cost Multipliers</>}>
              <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                Token usage is weighted by the model cost multiplier. A query using 1,000 tokens with a 1.5× multiplier counts as 1,500 weighted tokens.
              </Paragraph>

              <Descriptions column={1} bordered size="small">
                {Object.entries(MODEL_COSTS).map(([key, { label, multiplier }]) => (
                  <Descriptions.Item key={key} label={label}>
                    <Statistic value={multiplier} suffix="×" valueStyle={{ fontSize: 14 }} />
                  </Descriptions.Item>
                ))}
              </Descriptions>

              <Paragraph type="secondary" style={{ marginTop: 12, fontSize: 12 }}>
                Model cost multipliers are configured in the backend. Contact a developer to change them.
              </Paragraph>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
}

export default AISettings;
